import React, { useEffect } from "react";
import styled from "styled-components";

// Components
import { Header } from "./header";
import { Footer } from "./footer";
// import Transition from "../transition/transition"; // optional route fade — disabled for LCP/stability

// Context
import { useDraftProductsContext } from "../context/draft-products";

const Main = styled.main`
  flex: 1;

  margin: 190px 20px 90px 20px;

  &.page-search,
  &.page-our-story {
    margin: 190px 20px 20px 20px;
  }

  @media (max-width: 800px) {
    margin: 90px 15px 30px 15px;
  }
`;

export const App = ({
  location,
  children,
  pageType,
  isDesktop,
  showFooter,
  setFilterSettings,
}) => {
  const [, setDraftProducts] = useDraftProductsContext();

  // Fetch draft products only on pages that need them (shop, product pages)
  // Note: Shopify doesn't reliably send webhooks for status changes (draft ↔ active)
  // We fetch once per session and rely on build-time filtering as primary defense
  useEffect(() => {
    // Only fetch on pages that display products
    const productPages = ["shop", "artwork"];
    const currentPage = pageType.page;

    if (!productPages.includes(currentPage)) {
      return; // Don't fetch on other pages
    }

    let timeoutId = null;
    let resetTimeoutId = null;
    let isMounted = true;
    let isPageVisible = true;
    let hasFetched = false; // Prevent multiple simultaneous fetches

    // Pause polling when tab is hidden
    const handleVisibilityChange = () => {
      isPageVisible = !document.hidden;
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleFetchDraftProducts = async (forceRefresh = false) => {
      if (!isMounted || !isPageVisible || hasFetched) return;
      hasFetched = true;

      try {
        const url = `/.netlify/functions/get-all-draft-products?bust=${Date.now()}${forceRefresh ? "&force=true" : ""}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Cache-Control": "no-store",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.products && Array.isArray(data.products) && isMounted) {
          setDraftProducts(data.products);
        }
      } catch (error) {
        console.error("Failed to fetch draft products:", error);
        hasFetched = false; // Reset on error so we can retry
        if (isMounted && isPageVisible) {
          // Retry after 5 minutes on error (much longer to reduce calls)
          timeoutId = setTimeout(() => {
            if (isMounted) {
              hasFetched = false;
              handleFetchDraftProducts(false);
            }
          }, 5 * 60 * 1000);
        }
        return;
      }

      // Reset fetch flag after a delay to allow next poll (tracked for cleanup)
      resetTimeoutId = setTimeout(() => {
        if (isMounted) {
          hasFetched = false;
        }
      }, 1000);

      // Only poll if still on a product page and visible
      // Poll every 1 hour (minimal polling to reduce function calls)
      if (isMounted && productPages.includes(pageType.page) && isPageVisible) {
        timeoutId = setTimeout(() => {
          if (isMounted) {
            hasFetched = false;
            handleFetchDraftProducts(false);
          }
        }, 60 * 60 * 1000);
      }
    };

    // Fetch immediately on mount for product pages
    handleFetchDraftProducts(false);

    // Cleanup on unmount
    return () => {
      isMounted = false;
      hasFetched = true; // Prevent any pending fetches
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (resetTimeoutId) {
        clearTimeout(resetTimeoutId);
      }
    };
  }, [pageType.page]); // Removed setDraftProducts from deps - it's memoized now

  return (
    <>
      <Header
        location={location}
        pageBackgroundColor={pageType.background}
        fillColor={pageType.fillColor}
        isDesktop={isDesktop}
        setFilterSettings={setFilterSettings}
        pageType={pageType.page}
      />

      {/* <Transition location={location}><Main>…</Main></Transition> */}
      <Main className={`page-${pageType.page}`}>{children}</Main>

      {showFooter &&
        pageType.banner === true &&
        pageType.page !== `404` &&
        pageType.page !== `artwork` &&
        pageType.page !== `cart` && (
          <Footer
            location={location}
            bgColor={pageType.bannerBackground}
            hoverColor={pageType.hoverColor}
            underlineColor={pageType.underlineColor}
            showMailchimp={pageType.page !== `contact`}
          />
        )}
    </>
  );
};
